'use client';

import React, { useState } from 'react';
import { useServerInsertedHTML } from 'next/navigation';
import { ServerStyleSheet, StyleSheetManager, ThemeProvider } from 'styled-components';
import { createTheme } from '@/config/theme';
import { GlobalStyles } from '@/styles/GlobalStyles';

/**
 * StyledComponentsRegistry
 * 
 * Handles SSR for styled-components in Next.js App Router.
 * Note: ThemeProvider and GlobalStyles are now handled by ThemeContext
 * to enable dynamic theme switching. This registry only handles SSR stylesheet.
 */
export default function StyledComponentsRegistry({
  children,
}: {
  children: React.ReactNode;
}) {
  // Only create stylesheet once with lazy initial state
  // x-ref: https://reactjs.org/docs/hooks-reference.html#lazy-initial-state
  const [styledComponentsStyleSheet] = useState(() => new ServerStyleSheet());

  useServerInsertedHTML(() => {
    const styles = styledComponentsStyleSheet.getStyleElement();
    styledComponentsStyleSheet.instance.clearTag();
    return <>{styles}</>;
  });

  if (typeof window !== 'undefined') {
    // Client-side: ThemeProvider and GlobalStyles are handled by ThemeContext
    // Just render children
    return <>{children}</>;
  }

  // Server-side rendering: Use default light theme for SSR
  // The client will hydrate with the correct theme from ThemeContext
  const ssrTheme = createTheme('light');
  
  return (
    <StyleSheetManager sheet={styledComponentsStyleSheet.instance}>
      <ThemeProvider theme={ssrTheme}>
        <GlobalStyles />
        {children}
      </ThemeProvider>
    </StyleSheetManager>
  );
}
